/* @flow */

import * as React from 'react';
import { compose, withProps } from 'recompose';
import styled from '@emotion/styled';
import { withTheme } from 'emotion-theming';
import { css } from 'glamor';
import { padding, position } from 'polished';
import Responsive from 'app/components/responsive/Responsive';
import Tappable from 'app/components/commons/Tappable';
import HubLink from 'app/components/hubMenu/HubLink';
import imageCasino from 'app/images/hub/artwork_casino.jpg';
import imageFantasy from 'app/images/hub/artwork_sports.jpg';
import imageGambling from 'app/images/hub/artwork_racing.jpg';
import imageStables from 'app/images/hub/artwork_horses.jpg';
import imagePlayChips from 'app/images/hub/playchips.png';

type Props = {
  noScrollClassName: string,
  siteUrl: string
};

type State = {
  isOpen: boolean
};

export const hubMenuConfig = {
  colors: {
    background: '#02F082',
    text: 'black'
  },
  borderTopHeight: '3px',
  height: 28
};

const Wrapper = styled('div')(({ theme }) => ({
  backgroundColor: theme.color.basis1,
  fontFamily: '"Oswald"',
  position: 'relative',
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  [theme.mediaQuery.desktopAndUp]: {
    backgroundColor: 'transparent',
    ...position('absolute', 0, 0, null),
    zIndex: theme.zIndex.hub + 1
  }
}));

const Top = styled('div')(
  ({ theme }) => ({
    borderTop: `${hubMenuConfig.borderTopHeight} solid ${hubMenuConfig.colors.background}`,
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 0,
    position: 'relative',
    zIndex: theme.zIndex.hub + 1,
    height: `${hubMenuConfig.borderTopHeight}`
  }),
  ({ right, theme }) =>
    right
      ? {
          [theme.mediaQuery.desktopAndUp]: {
            backgroundColor: 'transparent',
            ...position('absolute', 0, '1em', null, null),
            fontSize: '2rem',
            borderTopColor: 'transparent'
          }
        }
      : {}
);

const ClickArea = styled('div')(
  {
    backgroundColor: `${hubMenuConfig.colors.background}`,
    color: `${hubMenuConfig.colors.text}`,
    height: '1.4rem',
    margin: '0 auto',
    ...padding(2, 5),
    position: 'relative',
    textTransform: 'uppercase',
    width: '7rem',
    '&::before, &::after': {
      borderColor: 'transparent',
      borderStyle: 'solid',
      content: '"\\00a0"',
      ...position('absolute', 0, null)
    },
    '&::before': {
      borderWidth: '0 1rem 1.4rem 0',
      borderRightColor: `${hubMenuConfig.colors.background}`,
      right: '100%'
    },
    '&::after': {
      borderWidth: '1.4rem 1rem 0 0',
      borderTopColor: `${hubMenuConfig.colors.background}`,
      left: '100%'
    }
  },
  ({ isOpen }) =>
    isOpen
      ? {
          fontSize: '2rem',
          backgroundColor: 'transparent',
          color: 'white',
          '&::before': {
            borderRightColor: 'transparent'
          },
          '&::after': {
            borderTopColor: 'transparent'
          }
        }
      : {}
);

const ClickInner = styled('div')({
  ...position('absolute', -3),
  textAlign: 'center',
  fontWeight: 'bold'
});

const Content = styled('div')(
  ({ theme }) => ({
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    overflow: 'hidden',
    ...position('fixed', 0),
    zIndex: theme.zIndex.hub,
    [theme.mediaQuery.desktopAndUp]: {
      backgroundColor: 'black',
      height: 110,
      ...position('absolute', '100%', 0, null)
    }
  }),
  ({ open }) => ({
    display: open ? 'block' : 'none'
  })
);

const List = styled('ul')(({ theme }) => ({
  margin: '0 auto',
  maxWidth: 400,
  padding: '4rem 2rem',
  [theme.mediaQuery.desktopAndUp]: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    maxWidth: 1200,
    padding: '0 3rem'
  }
}));

const Item = styled('li')(({ theme }) => ({
  display: 'block',
  margin: '5px 10px',
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: '25%',
    flexGrow: 0,
    flexShrink: 0
  },
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    margin: '15px 10px'
  }
}));

class HubMenu extends React.Component<Props, State> {
  props: Props;

  state: State = {
    isOpen: false
  };

  componentDidUpdate() {
    const { noScrollClassName } = this.props;
    const { isOpen } = this.state;

    if (isOpen) {
      window.scrollTo(0, 0);
      document.body.classList.add(noScrollClassName);
    } else {
      document.body.classList.remove(noScrollClassName);
    }
  }

  handleClick = () => {
    this.setState(state => ({
      isOpen: !state.isOpen
    }));
  };

  render() {
    const { isOpen } = this.state;
    const { siteUrl } = this.props;
    const { hostname } = window.location;

    return (
      <Responsive desktop>
        {isDesktop => (
          <Wrapper>
            <Top right={isDesktop && isOpen} isOpen={isOpen}>
              <Tappable
                component={ClickArea}
                right={isDesktop && isOpen}
                isOpen={isOpen}
                onClick={this.handleClick}
              >
                <ClickInner>
                  {isOpen ? <span className="ion-close" /> : <span>More games</span>}
                </ClickInner>
              </Tappable>
            </Top>
            <Content open={isOpen}>
              <List>
                <Item>
                  <HubLink href={siteUrl} image={imageFantasy} imagehover={imagePlayChips} active>
                    Fantasy Sports
                  </HubLink>
                </Item>
                <Item>
                  <HubLink
                    href="https://playbet.playup.com/"
                    image={imageGambling}
                    imagehover={imagePlayChips}
                    active={hostname === 'playbet.playup.com'}
                  >
                    Sports &amp; Racing
                  </HubLink>
                </Item>
                <Item>
                  <HubLink
                    href="https://stables.playup.com/"
                    image={imageStables}
                    imagehover={imagePlayChips}
                    smalltext="Coming Soon"
                    disabled
                  >
                    Fantasy Stables
                  </HubLink>
                </Item>
                <Item>
                  <HubLink
                    href="https://casino.playup.com/"
                    image={imageCasino}
                    imagehover={imagePlayChips}
                    smalltext="Coming Soon"
                    disabled
                  >
                    Poker &amp; Casino
                  </HubLink>
                </Item>
              </List>
            </Content>
          </Wrapper>
        )}
      </Responsive>
    );
  }
}

const enhancer = compose(
  withTheme,
  withProps(({ theme }) => ({
    noScrollClassName: css({
      [theme.mediaQuery.tabletLandscapeAndBelow]: {
        overflow: 'hidden'
      }
    }).toString()
  }))
);

export default enhancer(HubMenu);
